import { useEffect, useState, type ReactNode } from 'react';
import { ApiError, call } from '../api';
import { useApp } from '../context';
import { useIsOwner } from '../owner';
import { linkProps, navigate } from '../router';

/**
 * Jaaroverzicht ("Wrapped"): je jaar in tumedia als een reeks kaarten om doorheen te bladeren,
 * zoals Spotify Wrapped. Vanaf 1 december staat er een banner op Home.
 */

interface Title { title: string; key: string | null; thumb: string | null; poster: string | null; episodes?: number; plays?: number }
export interface WrappedData {
  year: number;
  scope: 'me' | 'all';
  name: string;
  plex: boolean;
  empty: boolean;
  totals: { hours: number; days: number; movies: number; episodes: number; shows: number; activeDays: number };
  months: { month: string; hours: number }[];
  bestMonth: string | null;
  peak: { hour: number; day: string };
  persona: { title: string; text: string };
  topShows: Title[];
  topMovies: Title[];
  genres: { genre: string; pct: number }[];
  binge: { hours: number; items: number; titles: string[]; at: number } | null;
  streak: { days: number; from: string; to: string } | null;
  first: { title: string; at: number } | null;
  music: { songs: number; artists: { name: string; n: number }[]; tracks: { title: string; n: number }[] };
  sport: { matches: number; leagues: { name: string; n: number }[]; recent: { title: string; league: string; day: string }[] };
  live: { channels: { name: string; n: number }[] };
  people: { name: string; hours: number }[] | null;
}

const dateNl = (ms: number) => new Date(ms).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' });

/** Banner op Home: van 1 december tot half januari (dan over het afgelopen jaar). */
export function WrappedBanner() {
  const now = new Date();
  const dec = now.getMonth() === 11;
  const jan = now.getMonth() === 0 && now.getDate() <= 15;
  const year = dec ? now.getFullYear() : now.getFullYear() - 1;
  const key = `tumedia.wrapped.closed.${year}`;
  const [closed, setClosed] = useState(() => {
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  if ((!dec && !jan) || closed) return null;
  return (
    <aside className="wr-banner" aria-label="Jaaroverzicht">
      <span className="wr-banner-year">{year}</span>
      <span className="wr-banner-text"><strong>Jouw {year} in tumedia</strong><span className="subtle">Wat je keek, luisterde en volgde, in een paar kaarten.</span></span>
      <a className="btn btn-primary" {...linkProps(`/wrapped/${year}`)}>Bekijken</a>
      <button className="icon-mini" aria-label="Sluiten" onClick={() => { setClosed(true); try { localStorage.setItem(key, '1'); } catch { /* geen opslag */ } }}>✕</button>
    </aside>
  );
}

export function Wrapped({ year, scope }: { year?: number; scope: 'me' | 'all' }) {
  const { plex } = useApp();
  const owner = useIsOwner();
  const [data, setData] = useState<WrappedData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [i, setI] = useState(0);
  const y = year ?? new Date().getFullYear();

  useEffect(() => {
    call<WrappedData>(`/api/wrapped?year=${y}&scope=${scope}`).then(setData).catch((e: unknown) => setError(e instanceof ApiError && e.message !== e.code ? e.message : 'Het jaaroverzicht kon niet geladen worden.'));
  }, [y, scope]);

  if (error) return <main className="page"><p className="placeholder-row error" role="alert">{error}</p></main>;
  if (!data) return <main className="wr-page"><div className="wr-card wr-c0"><p className="wr-small">Je jaar wordt opgeteld…</p></div></main>;

  const art = (t: Title) => t.poster ?? (t.thumb ? plex.image(t.thumb, 300, 450) : undefined);
  const slides: { id: string; body: ReactNode }[] = [];
  slides.push({
    id: 'intro',
    body: (
      <>
        <p className="wr-small">{data.scope === 'all' ? 'De hele server' : `Hoi ${data.name}`}</p>
        <h1 className="wr-huge">{data.year}</h1>
        <p className="wr-lead">{data.empty ? 'Er is voor dit jaar nog bijna niets bijgehouden. Kijk vooral verder en kom in december terug.' : 'Dit was jouw jaar in tumedia. Klaar?'}</p>
        {data.first && <p className="wr-small">Je begon het jaar op {dateNl(data.first.at)} met <b>{data.first.title}</b>.</p>}
      </>
    ),
  });
  if (data.totals.hours > 0) {
    const max = Math.max(1, ...data.months.map((m) => m.hours));
    slides.push({
      id: 'hours',
      body: (
        <>
          <p className="wr-small">Je keek dit jaar</p>
          <h1 className="wr-huge">{data.totals.hours.toLocaleString('nl-NL')}<span> uur</span></h1>
          <p className="wr-lead">Dat is {data.totals.days.toLocaleString('nl-NL')} dagen non-stop. Verspreid over {data.totals.activeDays} dagen: {data.totals.movies} films en {data.totals.episodes} afleveringen van {data.totals.shows} series.</p>
          <div className="wr-bars" aria-label="Uren per maand">
            {data.months.map((m) => (
              <span key={m.month} className={m.month === data.bestMonth ? 'is-top' : ''}>
                <i style={{ height: `${Math.max(3, (m.hours / max) * 100)}%` }} />
                <b>{m.month}</b>
              </span>
            ))}
          </div>
          {data.bestMonth && <p className="wr-small">Je drukste maand: <b>{data.bestMonth}</b>.</p>}
        </>
      ),
    });
  }
  if (data.topShows.length) {
    slides.push({
      id: 'shows',
      body: (
        <>
          <p className="wr-small">Jouw serie van het jaar</p>
          <div className="wr-hero">
            {art(data.topShows[0]) && <img src={art(data.topShows[0])} alt="" />}
            <div>
              <h1 className="wr-big">{data.topShows[0].title}</h1>
              <p className="wr-lead">{data.topShows[0].episodes} afleveringen</p>
            </div>
          </div>
          <ol className="wr-list" start={2}>
            {data.topShows.slice(1).map((s) => <li key={s.title}><span>{s.title}</span><b>{s.episodes} afl.</b></li>)}
          </ol>
        </>
      ),
    });
  }
  if (data.topMovies.length) {
    slides.push({
      id: 'movies',
      body: (
        <>
          <p className="wr-small">Jouw films</p>
          <div className="wr-posters">
            {data.topMovies.map((m, n) => (
              <figure key={m.title}>
                {art(m) ? <img src={art(m)} alt="" /> : <span className="wr-noart">{m.title}</span>}
                <figcaption><b>{n + 1}.</b> {m.title}{(m.plays ?? 0) > 1 ? ` · ${m.plays}×` : ''}</figcaption>
              </figure>
            ))}
          </div>
        </>
      ),
    });
  }
  if (data.genres.length) {
    slides.push({
      id: 'genres',
      body: (
        <>
          <p className="wr-small">Waar je naar keek</p>
          <h1 className="wr-big">{data.genres[0].genre}</h1>
          <div className="wr-genres">
            {data.genres.map((g) => <span key={g.genre}><b>{g.genre}</b><i style={{ width: `${g.pct}%` }} /><em>{g.pct}%</em></span>)}
          </div>
        </>
      ),
    });
  }
  if (data.totals.hours > 0) {
    slides.push({
      id: 'persona',
      body: (
        <>
          <p className="wr-small">Jij bent…</p>
          <h1 className="wr-big">{data.persona.title}</h1>
          <p className="wr-lead">{data.persona.text}</p>
          <ul className="wr-facts">
            <li><b>{String(data.peak.hour).padStart(2, '0')}:00</b><span>je favoriete kijkuur</span></li>
            <li><b>{data.peak.day}</b><span>je kijkdag</span></li>
            {data.binge && <li><b>{data.binge.hours} uur</b><span>langste sessie, {data.binge.items}× {data.binge.titles[0]} ({dateNl(data.binge.at)})</span></li>}
            {data.streak && <li><b>{data.streak.days} dagen</b><span>op rij iets gekeken</span></li>}
          </ul>
        </>
      ),
    });
  }
  if (data.music.songs > 0) {
    slides.push({
      id: 'music',
      body: (
        <>
          <p className="wr-small">Je luisterde {data.music.songs.toLocaleString('nl-NL')} nummers</p>
          {data.music.artists[0] && <h1 className="wr-big">{data.music.artists[0].name}</h1>}
          <p className="wr-lead">was je artiest van het jaar.</p>
          <div className="wr-two">
            <ol className="wr-list">{data.music.artists.map((a) => <li key={a.name}><span>{a.name}</span><b>{a.n}×</b></li>)}</ol>
            <ol className="wr-list">{data.music.tracks.map((t) => <li key={t.title}><span>{t.title}</span><b>{t.n}×</b></li>)}</ol>
          </div>
        </>
      ),
    });
  }
  if (data.sport.matches > 0 || data.live.channels.length) {
    slides.push({
      id: 'sport',
      body: (
        <>
          <p className="wr-small">Sport en Live TV</p>
          {data.sport.matches > 0 && <h1 className="wr-big">{data.sport.matches} wedstrijden</h1>}
          {data.sport.leagues[0] && <p className="wr-lead">Vooral {data.sport.leagues[0].name}{data.sport.leagues[1] ? ` en ${data.sport.leagues[1].name}` : ''}.</p>}
          <div className="wr-two">
            {data.sport.recent.length > 0 && <ol className="wr-list">{data.sport.recent.map((m) => <li key={m.title + m.day}><span>{m.title}</span><b>{m.league}</b></li>)}</ol>}
            {data.live.channels.length > 0 && <ol className="wr-list">{data.live.channels.map((c) => <li key={c.name}><span>{c.name}</span><b>{c.n}×</b></li>)}</ol>}
          </div>
        </>
      ),
    });
  }
  if (data.people?.length) {
    slides.push({
      id: 'people',
      body: (
        <>
          <p className="wr-small">Wie keek het meest</p>
          <ol className="wr-list wr-list-big">{data.people.map((p) => <li key={p.name}><span>{p.name}</span><b>{p.hours} uur</b></li>)}</ol>
        </>
      ),
    });
  }
  slides.push({
    id: 'outro',
    body: (
      <>
        <h1 className="wr-big">Op naar {data.year + 1}</h1>
        <p className="wr-lead">Bedankt voor het kijken. Zelfde tijd volgend jaar?</p>
        <div className="form-actions">
          <button className="btn btn-primary" onClick={() => setI(0)}>Nog een keer</button>
          {owner && <a className="btn" {...linkProps(`/wrapped/${data.year}/${data.scope === 'all' ? 'me' : 'all'}`)}>{data.scope === 'all' ? 'Mijn jaar' : 'De hele server'}</a>}
          <a className="btn" {...linkProps('/stats')}>Alle statistieken</a>
        </div>
      </>
    ),
  });

  const cur = Math.min(i, slides.length - 1);
  const go = (d: number) => setI((x) => Math.max(0, Math.min(slides.length - 1, x + d)));
  return (
    <main className={`wr-page wr-c${cur % 6}`} aria-label={`Jaaroverzicht ${data.year}`}>
      <div className="wr-dashes" aria-hidden="true">{slides.map((s, n) => <i key={s.id} className={n <= cur ? 'is-on' : ''} />)}</div>
      <section className="wr-card" key={slides[cur].id} aria-live="polite">{slides[cur].body}</section>
      <nav className="wr-nav" data-focus-group>
        <button className="btn" onClick={() => (cur === 0 ? navigate('/') : go(-1))}>{cur === 0 ? 'Sluiten' : 'Vorige'}</button>
        <span className="subtle">{cur + 1} / {slides.length}</span>
        {cur < slides.length - 1 && <button className="btn btn-primary" data-autofocus onClick={() => go(1)}>Volgende</button>}
      </nav>
    </main>
  );
}
